import React, { useId, useState } from 'react';

import { Input } from 'components/ui/input';

/**
 * Selector de fichero CSV, sin Chakra.
 *
 * `borderColor` se queda como estaba —el llamante pasa '#10B981' o '#EF4444' según haya fichero—,
 * así que va por `style`. Convertirlo en un booleano de validez sería más limpio, pero obliga a
 * tocar `OnboardingUserV2` y eso es otro cambio.
 *
 * ⚠️ Diferencia esperada: el borde de foco deja de ser el verde de `ProjectConfig` y pasa a ser el
 * anillo del sistema (`focus-visible:ring-ring`). Es la convergencia que busca la tarea, no un
 * descuido; se ve al tabular hasta el campo.
 *
 * 🔴 El mensaje de error ahora se anuncia. Antes era un `<p>` suelto: quien usa lector de pantalla
 * elegía un fichero del tipo equivocado, no pasaba nada perceptible y no había forma de saber por
 * qué. Con `aria-describedby` y `aria-invalid`, el campo dice que está mal y lee el motivo. Son dos
 * atributos y el texto ya existía.
 */
const FileUpload: React.FC<{ onFileUpload: (file: File) => void; borderColor: string }> = ({
	onFileUpload,
	borderColor
}) => {
	const [error, setError] = useState<string>('');
	const idError = useId();

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			// Validar tipo de archivo
			const validTypes = ['text/csv']; // Tipos permitidos
			if (!validTypes.includes(file.type)) {
				setError('Tipo de archivo no permitido. Solo se permiten CSV.');
				return;
			}

			// Validar tamaño de archivo (por ejemplo, 5 MB)
			const maxSizeInMB = 5;
			if (file.size > maxSizeInMB * 1024 * 1024) {
				setError(`El archivo debe ser menor a ${maxSizeInMB} MB.`);
				return;
			}

			setError('');
			onFileUpload(file);
		}
	};

	return (
		<div>
			<Input
				name="grossSalaryFile"
				type="file"
				accept=".csv, .xls, .xlsx, .ods"
				onChange={handleFileChange}
				style={{ borderColor }}
				aria-invalid={error ? true : undefined}
				aria-describedby={error ? idError : undefined}
			/>
			{error && (
				<p id={idError} role="alert" style={{ color: 'red' }}>
					{error}
				</p>
			)}
		</div>
	);
};

export default FileUpload;
